import { CheckCircle2, ChevronDown, Clock3, PhoneOff, UserRound } from 'lucide-react';
import {
  callRouteAttemptLabel,
  callRouteStageLabel,
  callRouteStrategyLabel,
  callRouteSummary,
  formatRouteWait,
} from '../CallRoutePresentation';
import type { CallRouteTrace } from '../types';

type CallRouteDetailsProps = {
  callId: string;
  trace?: CallRouteTrace | null;
  expanded: boolean;
  onToggle: () => void;
};

function attemptName(name: string, extension: string): string {
  const normalizedName = String(name || '').trim();
  if (normalizedName) return normalizedName;
  const normalizedExtension = String(extension || '').trim();
  return normalizedExtension ? `Добавочный ${normalizedExtension}` : 'Сотрудник не определён';
}

export function CallRouteDetails({ callId, trace, expanded, onToggle }: CallRouteDetailsProps) {
  const safeTrace = trace || null;
  const detailsId = `call-route-${callId}`;
  const summary = callRouteSummary(safeTrace);

  return <div className={`call-route-details ${expanded ? 'expanded' : ''}`}>
    <button
      type="button"
      className="call-route-summary"
      aria-expanded={expanded}
      aria-controls={detailsId}
      onClick={onToggle}
    >
      <span>{summary}</span>
      <ChevronDown aria-hidden="true" />
    </button>

    {expanded && <div
      id={detailsId}
      className="call-route-timeline"
      role="region"
      aria-label={`Фактический маршрут звонка: ${summary}`}
    >
      {!safeTrace || safeTrace.kind === 'LEGACY'
        ? <div className="call-route-legacy">
          <PhoneOff aria-hidden="true" />
          <span><b>Маршрут не фиксировался</b><small>Для этого звонка подробный маршрут ещё не сохранялся.</small></span>
        </div>
        : <>
          <header>
            <span><Clock3 aria-hidden="true" /> Общее ожидание: <b>{formatRouteWait(safeTrace.totalWaitSeconds)}</b></span>
            {safeTrace.answeredBy && <span><CheckCircle2 aria-hidden="true" /> Ответил: <b>{attemptName(safeTrace.answeredBy.name, safeTrace.answeredBy.extension)}</b></span>}
          </header>
          <ol>
            {safeTrace.stages.map(stage => <li key={`${stage.source}-${stage.stage}-${stage.groupId || 'direct'}`}>
              <i aria-hidden="true">{stage.stage}</i>
              <article>
                <div className="call-route-stage-head">
                  <span><b>{stage.groupName || 'Прямой звонок'}</b><small>{callRouteStrategyLabel(stage.strategy)}</small></span>
                  <span><em>{callRouteStageLabel(stage.outcome)}</em><small>Ожидание: {formatRouteWait(stage.waitSeconds)}</small></span>
                </div>
                {stage.attempts.length > 0 && <ul>
                  {stage.attempts.map(attempt => <li key={attempt.id}>
                    <UserRound aria-hidden="true" />
                    <span><b>{attemptName(attempt.operatorName, attempt.extension)}</b><small>{attempt.extension ? `доб. ${attempt.extension}` : 'без добавочного'}</small></span>
                    <em className={`outcome-${attempt.outcome.toLowerCase()}`}>{callRouteAttemptLabel(attempt.outcome)}</em>
                    <small>{formatRouteWait(attempt.waitSeconds)}</small>
                  </li>)}
                </ul>}
              </article>
            </li>)}
          </ol>
          {safeTrace.terminal.outcome === 'MISSED' && <div className="call-route-terminal missed">
            <PhoneOff aria-hidden="true" />
            Звонок завершился без ответа{safeTrace.terminal.groupName ? ` на этапе «${safeTrace.terminal.groupName}»` : ''}.
          </div>}
        </>}
    </div>}
  </div>;
}
